"use client";

import { ExternalLink, Github } from "lucide-react";
import { useState } from "react";

// Assets
import { Images } from "../assets/images";

type Project = {
  id: number;
  title: string;
  description: string;
  image: string;
  technologies: string[];
  githubUrl?: string;
  liveUrl?: string;
  category: "frontend" | "backend" | "fullstack";
};

const Projects = () => {
  const [activeFilter, setActiveFilter] = useState<string>("all");

  const projects: Project[] = [
    {
      id: 1,
      title: "Portfolio Personnel",
      description:
        "Mon portfolio personnel développé avec React, TypeScript et Tailwind CSS. Ce projet présente mes compétences et mes réalisations.",
      image: Images.portfolioImage,
      technologies: ["React", "TypeScript", "Tailwind CSS"],
      githubUrl: "https://github.com/ivanDev237/portfolio_website.git",
      liveUrl: "#",
      category: "frontend",
    },
    {
      id: 2,
      title: "API REST E-commerce",
      description:
        "Une API REST complète pour une application e-commerce, développée avec Node.js, Express et MongoDB.",
      image: Images.restApiImage,
      technologies: ["Node.js", "Express", "MySql", "JWT"],
      githubUrl: "https://github.com/johndoe/ecommerce-api",
      category: "backend",
    },
    {
      id: 3,
      title: "Application de Gestion de Tâches",
      description:
        "Une application fullstack de gestion de tâches avec authentification et fonctionnalités de collaboration.",
      image: Images.taskAiImages,
      technologies: ["React", "Node.js", "Express", "MongoDB", "Socket.io"],
      githubUrl: "https://github.com/johndoe/task-manager",

      category: "fullstack",
    },
    {
      id: 4,
      title: "Plateforme SEO avancée propulsée par l'IA",
      description:
        "Ce site vitrine présente une solution révolutionnaire pour améliorer la visibilité en ligne grâce à des outils SEO avancés, alimentés par l'intelligence artificielle",
      image: Images.AiLandingImage,
      technologies: [
        "Next.js",
        "React",
        "Tailwind CSS",
        "Framer Motion",
        "TypeScript",
      ],
      liveUrl: "https://ai-seo-showcase-website-zsoc.vercel.app/ ",
      category: "frontend",
    },
    {
      id: 5,
      title: "Application mobile sawa",
      description:
        "Application reservee aux entreprises et aux particuliers permettant a ceux ci de fideliser leurs clientele",
      image: Images.phoneMokup,
      technologies: ["React native", "Nest js", "TypeScript", "MySQL", "JWT"],
      category: "fullstack",
    },
    {
      id: 6,
      title: "Site vitrine du Zenith",
      description:
        "site vitrine reserve a une entreprise de construction nomme Zenith pour mettre en avant leurs services",
      image: Images.zenith,
      technologies: ["HTML", "js", "tailwind css"],
      category: "frontend",
      liveUrl: "https://zenith-plus.com/",
    },
  ];

  const filters = [
    { id: "all", name: "Tous" },
    { id: "frontend", name: "Frontend" },
    { id: "backend", name: "Backend" },
    { id: "fullstack", name: "Fullstack" },
  ];

  const filteredProjects =
    activeFilter === "all"
      ? projects
      : projects.filter((project) => project.category === activeFilter);

  return (
    <section id="projects" className="py-20 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Mes projets</h2>
          <div className="h-1 w-20 bg-primary mx-auto mb-8"></div>
          <p className="text-lg text-foreground/80">
            Découvrez une sélection de projets sur lesquels j'ai travaillé,
            démontrant mes compétences en développement web fullstack.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {filters.map((filter) => (
            <button
              key={filter.id}
              onClick={() => setActiveFilter(filter.id)}
              className={`px-6 py-2 rounded-full transition-colors ${
                activeFilter === filter.id
                  ? "bg-primary text-primary-foreground"
                  : "bg-card text-foreground/70 hover:bg-card/80"
              }`}
            >
              {filter.name}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project) => (
            <div
              key={project.id}
              className="bg-card rounded-lg overflow-hidden shadow-lg transition-transform hover:scale-105"
            >
              <div className="relative h-48 overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  className={`w-full h-full object-cover ${
                    project.id === 5 ? "object-top" : ""
                  } transition-transform hover:scale-110`}
                />
              </div>

              <div className="p-6">
                <h3 className="text-xl font-bold mb-2">{project.title}</h3>
                <p className="text-foreground/70 mb-4">{project.description}</p>

                <div className="flex flex-wrap gap-2 mb-6">
                  {project.technologies.map((tech) => (
                    <span
                      key={tech}
                      className="px-3 py-1 bg-primary/10 text-primary text-sm rounded-full"
                    >
                      {tech}
                    </span>
                  ))}
                </div>

                <div className="flex gap-4">
                  {project.githubUrl && (
                    <a
                      href={project.githubUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-foreground/70 hover:text-primary transition-colors"
                    >
                      <Github size={18} />
                      <span>Code</span>
                    </a>
                  )}

                  {project.liveUrl && (
                    <a
                      href={project.liveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-foreground/70 hover:text-primary transition-colors"
                    >
                      <ExternalLink size={18} />
                      <span>Demo</span>
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
